import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { DocumentNode, useQuery } from '@apollo/client';
import { useCompany } from './FetchConfig';
import getValue from '../components/connectedForm/utils/getValue';
import {
  FilterOperation,
  IFilterInput,
  IPurchasePaymentTerm,
  IReportTemplatesDropdownQueryResult,
  IReportTemplatesDropdownQueryVariables,
  ISalesPaymentTerm,
  Maybe,
  PaymentTermDate,
  PaymentInterval,
  ReportType,
} from '../graphql/schema/p4types';
import { addDays, addMonths, endOfMonth, startOfMonth } from 'date-fns';
import { useSelector } from 'react-redux';
import { IReduxStore } from '../@types/reduxTypes';
import { ReportTemplate } from '../graphql/entities';
import UserContext from '../components/Context/UserContext';

export const usePrecision = (valueType?: 'sales' | 'cost') => {
  const company = useCompany();
  return useMemo(() => {
    if (valueType === 'sales') return company?.sellingPricePrecision ?? 2;
    if (valueType === 'cost') return company?.costPricePrecision ?? 2;
    return 2;
  }, [company?.costPricePrecision, company?.sellingPricePrecision, valueType]);
};

//Wraps function into a ref to make sure we always call with up to date values (Useful for grids as the function may change, but the grid won't necessarily re-render due to it).
//Must be a better way (Maybe pass the function down through context, as then the relevant child component 'will' rerender due to the context change), but this works for the moment.
export const useRefFunction = <F = Function>(func: F) => {
  const withRowUpdatedRef = useRef<F>(func);
  withRowUpdatedRef.current = func;
  return useCallback(
    (...args: any[]) => (withRowUpdatedRef.current as unknown as Function)(...args),
    []
  ) as unknown as F;
};

export const useValueState = <V = any>(initialState: V): [V, React.Dispatch<React.SetStateAction<V>>] => {
  const [value, setValue] = useState<V>(initialState);
  const setter = useCallback((val: any) => setValue(getValue(val)), [setValue]) as typeof setValue;
  return [value, setter];
};

//Just a helper to get aggregation data for an entity.
//First argument can be simply the entity (e.g. PurchaseCredit), or can pass just the aggregate graphQL query.
//
//For the moment just used to get the count - Expect in the future to add additional parameters to get column totals or similar information.
export const useAggregateQuery = ({ aggregate }: { aggregate: DocumentNode }, filters: Array<IFilterInput>) => {
  const { data } = useQuery(aggregate, {
    variables: {
      filters: filters,
    },
  });
  return data?.listData;
};

/**
 * Calculate a due date from the provided base date and payment term.
 */
export const calculatePaymentDueDate = (
  baseDate: Date,
  paymentTerm?: Maybe<IPurchasePaymentTerm | ISalesPaymentTerm>
): Date => {
  if (!paymentTerm) {
    return baseDate;
  }

  let baseDueDate;
  switch (paymentTerm.paymentDate) {
    case PaymentTermDate.PeriodStart:
      baseDueDate = startOfMonth(baseDate);
      break;
    case PaymentTermDate.PeriodEnd:
      baseDueDate = endOfMonth(baseDate);
      break;

    default:
      baseDueDate = baseDate;
  }

  switch (paymentTerm.paymentInterval) {
    case PaymentInterval.Months:
      return addMonths(baseDueDate, paymentTerm.paymentDays);
    case PaymentInterval.MonthsPlus:
      return endOfMonth(addMonths(baseDueDate, paymentTerm.paymentDays));
    default:
      return addDays(baseDueDate, paymentTerm.paymentDays);
  }
};

/**
 * Helper hook to discourage use of AppContext to fetch this information.
 * UserContext is the most appropriate way to access this information.
 */
export const useFullUserDetails = () => {
  const { currentUser } = useContext(UserContext);
  return currentUser;
};

export const useReportTemplatesQuery = (reportType: ReportType) => {
  const { data, ...rest } = useQuery<IReportTemplatesDropdownQueryResult, IReportTemplatesDropdownQueryVariables>(
    ReportTemplate.dropdown.query,
    {
      variables: {
        filters: [
          {
            filterOperation: FilterOperation.Equals,
            filterParameters: {
              field: 'reportType',
              value: reportType,
            },
          },
        ],
      },
    }
  );

  return useMemo(() => {
    const templates = data?.list?.edges?.map((edge) => edge?.node) ?? [];
    return { data, templates, ...rest };
  }, [data, rest]);
};

export const useCheckUserIsAdmin = () => {
  return useSelector((store: IReduxStore) => store?.app?.fullUserDetails?.systemUser?.admin);
};
